// src/pages/ppic/UoM.jsx
import React, { useState, useEffect } from "react";
import {
    Typography,
    Box,
    Breadcrumbs,
    Tabs,
    Tab,
    Paper,
    useMediaQuery,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Link as MuiLink // Import Link from Material-UI for clickable breadcrumbs
} from "@mui/material";

import { useTheme } from "@mui/material/styles";
import UoMConfig from "./UoMConfig"; // Import the new UoMConfig
import useAuthCheck from "../../hooks/useAuthCheck";
import { useOutletContext, useNavigate } from "react-router-dom";

// Define tab configurations outside the component to avoid unnecessary re-renders.
const tabConfigurations = [
    {
        label: "Kode Material",
        dbPath: "db_warehouse/db_ppic/db_config/tb_kode_material",
        fieldName: "kode_material",
        primaryKeyField: "id_kode_material",
    },
    {
        label: "UoM",
        dbPath: "db_warehouse/db_ppic/db_config/tb_uom",
        fieldName: "nama_uom",
        primaryKeyField: "id_uom",
    },
];

function UoM() {
    const { user, loading } = useAuthCheck();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const navigate = useNavigate();

    const { breadcrumbs } = useOutletContext();

    const [tabIndex, setTabIndex] = useState(() => {
        const storedTabIndex = localStorage.getItem('uomTabIndex');
        const parsedIndex = parseInt(storedTabIndex, 10);
        if (!isNaN(parsedIndex) && parsedIndex >= 0 && parsedIndex < tabConfigurations.length) {
            return parsedIndex;
        }
        return 0; // Default to the first tab
    });

    useEffect(() => {
        localStorage.setItem('uomTabIndex', tabIndex.toString());
    }, [tabIndex]);

    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    You must be logged in to access this page.
                </Alert>
            </Box>
        );
    }

    const currentTabConfig = tabConfigurations[tabIndex];

    return (
        <Box sx={{ pb: isMobile ? 8 : 0 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                {breadcrumbs && breadcrumbs.map((crumb, index) => (
                    crumb.path ? (
                        <MuiLink
                            key={index}
                            underline="hover"
                            color="inherit"
                            onClick={() => navigate(crumb.path)}
                            sx={{ cursor: 'pointer' }}
                        >
                            {crumb.label}
                        </MuiLink>
                    ) : (
                        <Typography key={index} color="text.primary">
                            {crumb.label}
                        </Typography>
                    )
                ))}
            </Breadcrumbs>

            {/* Layout */}
            <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 4 }}>
                {/* Main content */}
                <Box sx={{ flex: 1 }}>
                    <Card>
                        <CardContent>
                            {/* Render UoMConfig dynamically based on tabIndex */}
                            {currentTabConfig && (
                                <UoMConfig
                                    title={`Daftar ${currentTabConfig.label}`}
                                    dbPath={currentTabConfig.dbPath}
                                    fieldName={currentTabConfig.fieldName}
                                    primaryKeyField={currentTabConfig.primaryKeyField}
                                />
                            )}
                        </CardContent>
                    </Card>
                </Box>

                {/* Desktop Sidebar (Tabs) */}
                {!isMobile && (
                    <Paper
                        elevation={1}
                        sx={{
                            alignSelf: "flex-start",
                        }}
                    >
                        <Tabs
                            orientation="vertical"
                            value={tabIndex}
                            onChange={handleTabChange}
                            TabIndicatorProps={{
                                sx: {
                                    left: 0,
                                    right: "auto",
                                    width: "4px",
                                    bgcolor: "primary.main",
                                },
                            }}
                            sx={{
                                width: 350,
                                borderLeft: 1,
                                borderColor: "divider",
                                ".MuiTab-root": {
                                    alignItems: "flex-start",
                                    justifyContent: "flex-start",
                                    px: 2,
                                },
                            }}
                        >
                            {/* Loop through tab configurations for sidebar */}
                            {tabConfigurations.map((tab, index) => (
                                <Tab key={index} label={tab.label} />
                            ))}
                        </Tabs>
                    </Paper>
                )}
            </Box>

            {/* Bottom Tabs (Mobile) */}
            {isMobile && (
                <Paper
                    sx={{
                        position: "fixed",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        borderTop: 1,
                        borderColor: "divider",
                        zIndex: 1000,
                    }}
                    elevation={3}
                >
                    <Tabs
                        value={tabIndex}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons
                        allowScrollButtonsMobile
                        TabIndicatorProps={{
                            sx: { bgcolor: "primary.main", height: 3 },
                        }}
                        centered={false}
                    >
                        {/* Loop through tab configurations for mobile */}
                        {tabConfigurations.map((tab, index) => (
                            <Tab key={index} label={tab.label} />
                        ))}
                    </Tabs>
                </Paper>
            )}
        </Box>
    );
}

export default UoM;
